// FriendManager - Handles friend system functionality
// Friend requests by code, accept/decline, friend list

import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
    addDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js';
import { db, isConfigured } from './config.js';

/**
 * @typedef {Object} FriendRequest
 * @property {string} id - Request document ID
 * @property {string} from - Sender user ID
 * @property {string} to - Recipient user ID
 * @property {string} fromDisplayName - Sender display name
 * @property {string} fromFriendCode - Sender friend code
 * @property {string} status - 'pending' | 'accepted' | 'declined'
 * @property {Date} createdAt - When request was sent
 */

/**
 * @typedef {Object} Friend
 * @property {string} oderId - Friend's user ID
 * @property {string} displayName - Friend's display name
 * @property {string} friendCode - Friend's code
 * @property {object} stats - Friend's public stats
 */

export class FriendManager {
    constructor(accountManager) {
        this.accountManager = accountManager;
        this.friendsCache = null;
        this.friendsCacheTimestamp = 0;
        this.cacheTTL = 60000; // 1 minute
        this.friendListeners = [];
    }

    /**
     * Register listener for friend list changes
     */
    onFriendsChanged(callback) {
        this.friendListeners.push(callback);
        return () => {
            const index = this.friendListeners.indexOf(callback);
            if (index > -1) this.friendListeners.splice(index, 1);
        };
    }

    /**
     * Notify listeners of friend list changes
     */
    _notifyFriendsChanged() {
        this.friendListeners.forEach(cb => {
            try {
                cb();
            } catch (e) {
                console.error('[FriendManager] Listener error:', e);
            }
        });
    }

    /**
     * Send friend request by friend code
     * @param {string} friendCode - 8-character friend code
     */
    async sendFriendRequest(friendCode) {
        if (!isConfigured || !db) {
            throw new Error('Cloud features not available');
        }

        const currentUserId = this.accountManager.getUserId();
        if (!currentUserId) {
            throw new Error('Must be signed in');
        }

        // Normalize code
        friendCode = friendCode.toUpperCase().trim();

        if (friendCode.length !== 8) {
            throw new Error('Friend code must be 8 characters');
        }

        // Find user by friend code
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('friendCode', '==', friendCode));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            throw new Error('Friend code not found');
        }

        const targetDoc = snapshot.docs[0];
        const targetUserId = targetDoc.id;
        const targetData = targetDoc.data();

        // Check if trying to add self
        if (targetUserId === currentUserId) {
            throw new Error('Cannot add yourself as a friend');
        }

        // Check if already friends
        const existingFriendship = await this._checkExistingFriendship(currentUserId, targetUserId);
        if (existingFriendship) {
            throw new Error('Already friends with this user');
        }

        // Check for existing pending request
        const existingRequest = await this._checkExistingRequest(currentUserId, targetUserId);
        if (existingRequest) {
            throw new Error('Friend request already sent');
        }

        // Check for incoming request (auto-accept if they sent one too)
        const incomingRequest = await this._checkExistingRequest(targetUserId, currentUserId);
        if (incomingRequest) {
            // Auto-accept their request
            await this.acceptFriendRequest(incomingRequest.id);
            return { autoAccepted: true };
        }

        // Get current user's info
        const currentUserDoc = await getDoc(doc(db, 'users', currentUserId));
        const currentUserData = currentUserDoc.exists() ? currentUserDoc.data() : {};

        // Create friend request
        await addDoc(collection(db, 'friendRequests'), {
            from: currentUserId,
            to: targetUserId,
            fromDisplayName: currentUserData.displayName || 'Anonymous Driver',
            fromFriendCode: currentUserData.friendCode || '',
            toDisplayName: targetData.displayName || 'Anonymous Driver',
            toFriendCode: targetData.friendCode || '',
            status: 'pending',
            createdAt: serverTimestamp()
        });

        console.log('[FriendManager] Friend request sent to:', targetUserId);
        return { autoAccepted: false };
    }

    /**
     * Check if friendship already exists
     */
    async _checkExistingFriendship(userId1, userId2) {
        const sortedIds = userId1 < userId2 ? [userId1, userId2] : [userId2, userId1];
        const friendshipId = `${sortedIds[0]}_${sortedIds[1]}`;
        const friendshipRef = doc(db, 'friends', friendshipId);
        const snapshot = await getDoc(friendshipRef);
        return snapshot.exists();
    }

    /**
     * Check for existing pending request
     */
    async _checkExistingRequest(fromId, toId) {
        const requestsRef = collection(db, 'friendRequests');
        const q = query(
            requestsRef,
            where('from', '==', fromId),
            where('to', '==', toId),
            where('status', '==', 'pending')
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) return null;
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }

    /**
     * Get pending friend requests (incoming)
     * @returns {Promise<FriendRequest[]>}
     */
    async getPendingRequests() {
        if (!isConfigured || !db) return [];

        const currentUserId = this.accountManager.getUserId();
        if (!currentUserId) return [];

        try {
            const requestsRef = collection(db, 'friendRequests');
            const q = query(
                requestsRef,
                where('to', '==', currentUserId),
                where('status', '==', 'pending'),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(q);

            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date()
            }));
        } catch (error) {
            console.error('[FriendManager] Failed to get pending requests:', error);
            return [];
        }
    }

    /**
     * Get sent friend requests (outgoing)
     * @returns {Promise<FriendRequest[]>}
     */
    async getSentRequests() {
        if (!isConfigured || !db) return [];

        const currentUserId = this.accountManager.getUserId();
        if (!currentUserId) return [];

        try {
            const requestsRef = collection(db, 'friendRequests');
            const q = query(
                requestsRef,
                where('from', '==', currentUserId),
                where('status', '==', 'pending')
            );
            const snapshot = await getDocs(q);

            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date()
            }));
        } catch (error) {
            console.error('[FriendManager] Failed to get sent requests:', error);
            return [];
        }
    }

    /**
     * Accept a friend request
     * @param {string} requestId - Friend request document ID
     */
    async acceptFriendRequest(requestId) {
        if (!isConfigured || !db) {
            throw new Error('Cloud features not available');
        }

        const requestRef = doc(db, 'friendRequests', requestId);
        const requestSnap = await getDoc(requestRef);

        if (!requestSnap.exists()) {
            throw new Error('Friend request not found');
        }

        const request = requestSnap.data();
        const currentUserId = this.accountManager.getUserId();

        // Verify current user is the recipient
        if (request.to !== currentUserId) {
            throw new Error('Cannot accept request sent to another user');
        }

        // Create friendship document
        const userId1 = request.from;
        const userId2 = request.to;
        const sortedIds = userId1 < userId2 ? [userId1, userId2] : [userId2, userId1];
        const friendshipId = `${sortedIds[0]}_${sortedIds[1]}`;

        const friendshipRef = doc(db, 'friends', friendshipId);
        await setDoc(friendshipRef, {
            users: sortedIds,
            createdAt: serverTimestamp(),
            status: 'accepted'
        });

        // Delete the request
        await deleteDoc(requestRef);

        // Invalidate cache
        this._invalidateCache();

        console.log('[FriendManager] Friend request accepted');
    }

    /**
     * Decline a friend request
     * @param {string} requestId - Friend request document ID
     */
    async declineFriendRequest(requestId) {
        if (!isConfigured || !db) {
            throw new Error('Cloud features not available');
        }

        const requestRef = doc(db, 'friendRequests', requestId);
        const requestSnap = await getDoc(requestRef);

        if (!requestSnap.exists()) {
            throw new Error('Friend request not found');
        }

        const request = requestSnap.data();
        const currentUserId = this.accountManager.getUserId();

        // Verify current user is the recipient
        if (request.to !== currentUserId) {
            throw new Error('Cannot decline request sent to another user');
        }

        // Delete the request
        await deleteDoc(requestRef);

        console.log('[FriendManager] Friend request declined');
    }

    /**
     * Cancel a sent friend request
     * @param {string} requestId - Friend request document ID
     */
    async cancelFriendRequest(requestId) {
        if (!isConfigured || !db) {
            throw new Error('Cloud features not available');
        }

        const requestRef = doc(db, 'friendRequests', requestId);
        const requestSnap = await getDoc(requestRef);

        if (!requestSnap.exists()) {
            throw new Error('Friend request not found');
        }

        const request = requestSnap.data();
        const currentUserId = this.accountManager.getUserId();

        // Verify current user is the sender
        if (request.from !== currentUserId) {
            throw new Error('Cannot cancel request sent by another user');
        }

        await deleteDoc(requestRef);
        console.log('[FriendManager] Friend request cancelled');
    }

    /**
     * Get list of friend user IDs
     * @returns {Promise<string[]>}
     */
    async getFriendIds() {
        if (!isConfigured || !db) return [];

        // Check cache
        if (this.friendsCache && Date.now() - this.friendsCacheTimestamp < this.cacheTTL) {
            return this.friendsCache;
        }

        const currentUserId = this.accountManager.getUserId();
        if (!currentUserId) return [];

        try {
            const friendsRef = collection(db, 'friends');
            const q = query(friendsRef, where('users', 'array-contains', currentUserId));
            const snapshot = await getDocs(q);

            const friendIds = snapshot.docs.map(doc => {
                const users = doc.data().users;
                return users.find(u => u !== currentUserId);
            }).filter(Boolean);

            // Cache results
            this.friendsCache = friendIds;
            this.friendsCacheTimestamp = Date.now();

            return friendIds;
        } catch (error) {
            console.error('[FriendManager] Failed to get friend IDs:', error);
            return this.friendsCache || [];
        }
    }

    /**
     * Get full friend list with details
     * @returns {Promise<Friend[]>}
     */
    async getFriends() {
        if (!isConfigured || !db) return [];

        const friendIds = await this.getFriendIds();
        const friends = [];

        for (const friendId of friendIds) {
            try {
                // Get user document
                const userRef = doc(db, 'users', friendId);
                const userSnap = await getDoc(userRef);

                // Get profile for stats
                const profileRef = doc(db, 'users', friendId, 'profile', 'public');
                const profileSnap = await getDoc(profileRef);

                const userData = userSnap.exists() ? userSnap.data() : {};
                const profileData = profileSnap.exists() ? profileSnap.data() : {};

                friends.push({
                    oderId: friendId,
                    displayName: userData.displayName || 'Anonymous Driver',
                    friendCode: userData.friendCode || '',
                    stats: profileData.publicStats || {}
                });
            } catch (error) {
                console.error('[FriendManager] Failed to get friend details:', friendId, error);
            }
        }

        return friends;
    }

    /**
     * Remove a friend
     * @param {string} friendId - Friend's user ID
     */
    async removeFriend(friendId) {
        if (!isConfigured || !db) {
            throw new Error('Cloud features not available');
        }

        const currentUserId = this.accountManager.getUserId();
        if (!currentUserId) {
            throw new Error('Must be signed in');
        }

        const sortedIds = currentUserId < friendId ? [currentUserId, friendId] : [friendId, currentUserId];
        const friendshipId = `${sortedIds[0]}_${sortedIds[1]}`;

        const friendshipRef = doc(db, 'friends', friendshipId);
        const friendshipSnap = await getDoc(friendshipRef);

        if (!friendshipSnap.exists()) {
            throw new Error('Friendship not found');
        }

        await deleteDoc(friendshipRef);
        this._invalidateCache();

        console.log('[FriendManager] Friend removed');
    }

    /**
     * Get friend count
     */
    async getFriendCount() {
        const friendIds = await this.getFriendIds();
        return friendIds.length;
    }

    /**
     * Invalidate cache
     */
    _invalidateCache() {
        this.friendsCache = null;
        this.friendsCacheTimestamp = 0;
        this._notifyFriendsChanged();
    }

    /**
     * Clear cache manually
     */
    clearCache() {
        this._invalidateCache();
    }
}
